"use client"

/**
 * Widget Chat Preview
 *
 * Static mockup that mirrors the real widget appearance.
 * Reacts to config changes in real time.
 */

import { useRef } from "react"
import Image from "next/image"
import type { WebWidgetConfig } from "@/lib/agents/messaging/types"
import {
  darkenColor,
  getFontCss,
  usesDefaultBrandingText,
  WIDGET_BUBBLE_OFFSET,
  WIDGET_BUBBLE_SIZE,
} from "@/lib/agents/messaging/channels/web-widget/constants"
import { DEFAULT_OFFLINE_MESSAGE } from "@/lib/agents/messaging/channels/shared/offline-message"

interface WidgetPreviewProps {
  config: WebWidgetConfig
  /**
   * `floating` (default) mirrors the embedded `web_widget`: chat window on
   * top + a mini-screen below showing the bubble / offset. `inline` drops
   * the bubble mini-screen because the demo widget is always inline inside
   * a landing container — there is no floating launcher to preview.
   */
  mode?: "floating" | "inline"
  /** Hidden for web_demo — its widget never renders the offline notice. */
  showOfflineNotice?: boolean
}

/**
 * Width of the preview frame in pixels. Exported so sibling UI elements
 * (e.g. the AI Output Format info block in `web-widget-settings.tsx`) can
 * align to the same width without hardcoding the value.
 */
export const WIDGET_PREVIEW_WIDTH = 340

const SAMPLE_MESSAGES: { text: string; direction: "inbound" | "outbound"; time: string }[] = [
  { text: "Hi! I have a question about your pricing plans.", direction: "inbound", time: "Mar 31, 02:22 pm" },
  { text: "Of course! I'd be happy to help. We offer three plans: Starter, Growth, and Pro. What are you looking for?", direction: "outbound", time: "Mar 31, 02:22 pm" },
  { text: "What's included in the Growth plan?", direction: "inbound", time: "Mar 31, 02:24 pm" },
]

export function WidgetPreview({ config, mode = "floating", showOfflineNotice = true }: WidgetPreviewProps) {
  const bubbleRef = useRef<HTMLDivElement>(null)
  const primaryColor = config.primaryColor || "#4F46E5"
  const title = config.title || ""
  const showBranding = config.showBranding !== false
  const borderRadius = config.borderRadius ?? 12
  const isOutline = config.bubbleStyle === "outline"
  const isGradient = config.headerStyle === "gradient"
  const bubbleAnimation = config.bubbleAnimation ?? "bounce"
  const fontSize = config.fontSize ?? 14
  const offsetX = config.bubbleOffsetX ?? WIDGET_BUBBLE_OFFSET.default
  const offsetY = config.bubbleOffsetY ?? WIDGET_BUBBLE_OFFSET.default
  const isLeft = config.position === "bottom-left"
  // Scaled so the largest size and offsets keep the bubble inside the 340×160 box
  const scaledOffsetX = Math.round(offsetX * 0.6)
  const scaledOffsetY = Math.round(offsetY * 0.35)
  const scaledSize = Math.round((config.bubbleSize ?? WIDGET_BUBBLE_SIZE.default) * 0.8)
  const scaledIconSize = Math.round(scaledSize * 0.46)
  const fontFamily = getFontCss(config.fontFamily)

  const headerBg = isGradient
    ? `linear-gradient(135deg, ${primaryColor}, ${darkenColor(primaryColor, 40)})`
    : primaryColor

  return (
    <div className="flex flex-col items-center">
      <p className="text-xs text-gray-500 mb-3">Preview</p>
      {/* Load Google Font for preview */}
      {config.fontFamily && config.fontFamily !== "System" && (
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css2?family=${encodeURIComponent(config.fontFamily)}:wght@400;600&display=swap`}
        />
      )}
      <div
        className="overflow-hidden shadow-lg border border-gray-200 flex flex-col"
        style={{ width: WIDGET_PREVIEW_WIDTH, height: 500, borderRadius, fontFamily, fontSize }}
      >
        {/* Header */}
        <div
          className="flex items-center gap-2.5 px-4 py-3"
          style={{ background: headerBg, borderRadius: `${borderRadius}px ${borderRadius}px 0 0` }}
        >
          {config.avatarUrl && (
            <Image
              src={config.avatarUrl}
              alt=""
              width={36}
              height={36}
              className="w-9 h-9 rounded-full object-cover"
              style={{ background: "rgba(255,255,255,0.2)" }}
              unoptimized
            />
          )}
          <div className="flex flex-col">
            {title && <span className="text-white font-semibold text-[15px] leading-tight">{title}</span>}
            {config.headerSubtitle && (
              <span className="text-white/70 text-[11px] leading-tight mt-0.5">{config.headerSubtitle}</span>
            )}
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 min-h-0 overflow-hidden bg-white px-4 py-3 flex flex-col gap-4">
          {config.welcomeMessage && (
            <p className="text-center text-gray-500 py-2 px-4 leading-relaxed">
              {config.welcomeMessage}
            </p>
          )}

          {SAMPLE_MESSAGES.map((msg, i) => (
            <div
              key={i}
              className="flex flex-col max-w-[80%]"
              style={{ alignSelf: msg.direction === "inbound" ? "flex-end" : "flex-start" }}
            >
              <div
                className="px-3.5 py-2.5 leading-[1.4]"
                style={{
                  background: msg.direction === "inbound" ? primaryColor : "#f3f4f6",
                  color: msg.direction === "inbound" ? "#ffffff" : "#1f2937",
                  borderRadius: "16px",
                  ...(msg.direction === "inbound"
                    ? { borderBottomRightRadius: "4px" }
                    : { borderBottomLeftRadius: "4px" }),
                }}
              >
                {msg.text}
              </div>
              <span
                className="text-[10px] text-gray-400 mt-1"
                style={{ alignSelf: msg.direction === "inbound" ? "flex-end" : "flex-start", paddingInline: "4px" }}
              >
                {msg.time}
              </span>
            </div>
          ))}

          {/* Offline notice preview — visitors see this while AI replies are off */}
          {showOfflineNotice && (
            <p className="text-center text-[13px] leading-relaxed rounded-[10px] px-3.5 py-2.5 mx-2 bg-amber-100 text-amber-800">
              {config.offline_message?.text || DEFAULT_OFFLINE_MESSAGE}
            </p>
          )}
        </div>

        {/* Input area */}
        <div className="flex items-end gap-2 px-4 py-3 border-t border-gray-200 bg-white">
          <div className="flex-1 border border-gray-300 rounded-[20px] px-4 py-2 text-gray-400" style={{ minHeight: 50 }}>
            Type a message...
          </div>
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: primaryColor }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </div>
        </div>

        {/* Branding */}
        {showBranding && (
          <div className="text-center py-1.5 text-[11px] text-gray-400 bg-white" style={{ borderRadius: `0 0 ${borderRadius}px ${borderRadius}px` }}>
            {usesDefaultBrandingText(config.brandingText)
              ? <>Powered by <span className="text-gray-500">Versafy.ai</span></>
              : config.brandingText || "\u00A0"}
          </div>
        )}
      </div>

      {/* Chat bubble — positioned inside a mini "screen" to visualize offsets.
          Hidden for `inline` mode (demo widget has no floating launcher). */}
      {mode === "floating" && (
      <div
        className="relative mt-3 border border-dashed border-gray-300 rounded-lg bg-gray-50/50"
        style={{ width: WIDGET_PREVIEW_WIDTH, height: 160 }}
      >
        {/* Offset guides */}
        {(offsetX !== WIDGET_BUBBLE_OFFSET.default || offsetY !== WIDGET_BUBBLE_OFFSET.default) && (
          <>
            <div
              className="absolute border-r border-dashed border-blue-300"
              style={{
                [isLeft ? "left" : "right"]: 0,
                bottom: 0,
                width: scaledOffsetX,
                height: "100%",
              }}
            />
            <div
              className="absolute border-t border-dashed border-blue-300"
              style={{
                [isLeft ? "left" : "right"]: 0,
                bottom: 0,
                width: "100%",
                height: scaledOffsetY,
              }}
            />
          </>
        )}
        <div
          ref={bubbleRef}
          className="absolute rounded-full shadow-lg flex items-center justify-center cursor-pointer"
          title="Click to preview animation"
          style={{
            background: isOutline ? "#ffffff" : primaryColor,
            border: isOutline ? `2px solid ${primaryColor}` : "none",
            width: scaledSize,
            height: scaledSize,
            bottom: scaledOffsetY,
            [isLeft ? "left" : "right"]: scaledOffsetX,
          }}
          onClick={() => {
            const el = bubbleRef.current
            if (!el || bubbleAnimation === "none") return
            el.style.animation = "none"
            void el.offsetHeight
            const animations: Record<string, string> = {
              bounce: "vwp-bounce 1s ease 1",
              pulse: `vwp-pulse 1.5s ease 2`,
              wiggle: "vwp-wiggle 0.8s ease 1",
            }
            el.style.animation = animations[bubbleAnimation] || ""
          }}
        >
          <svg width={scaledIconSize} height={scaledIconSize} viewBox="0 0 24 24" fill="none" stroke={isOutline ? primaryColor : "white"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <span className="absolute top-1 left-1/2 -translate-x-1/2 text-[9px] text-gray-400">
          {bubbleAnimation !== "none" ? "Click bubble to preview" : "Button position"}
        </span>
      </div>
      )}

      {/* Keyframes for preview animations */}
      <style>{`
        @keyframes vwp-bounce {
          0%,20%,50%,80%,100% { transform: translateY(0); }
          40% { transform: translateY(-16px); }
          60% { transform: translateY(-8px); }
        }
        @keyframes vwp-pulse {
          0% { box-shadow: 0 0 0 0 ${primaryColor}80; }
          70% { box-shadow: 0 0 0 14px ${primaryColor}00; }
          100% { box-shadow: 0 0 0 0 ${primaryColor}00; }
        }
        @keyframes vwp-wiggle {
          0%,100% { transform: rotate(0); }
          15% { transform: rotate(-12deg); }
          30% { transform: rotate(10deg); }
          45% { transform: rotate(-8deg); }
          60% { transform: rotate(6deg); }
          75% { transform: rotate(-2deg); }
        }
      `}</style>
    </div>
  )
}
